import Api from 'client/api/v2/Api';
import CsrfApi from 'client/api/v2/CsrfApi';

class SignupApi extends Api {
  initialize() {
    this.axios.interceptors.request.use(async (config) => {
      const result = await CsrfApi.confirmCsrf(this.csrfUrl, this.csrfHeader, config, this.axios);
      return result instanceof Error ? Promise.reject(result) : Promise.resolve(result);
    }, (error) => Promise.reject(error));

    return CsrfApi.fetchCsrfToken(this.csrfUrl, this.csrfHeader, this.axios);
  }

  /**
   * Send a request to confirm the registration of a new admin using a 6-digit registration code
   * sent to the admin via a confirmation email
   * @param {String} email - The admin's email address
   * @param {String} registrationCode - The 6-digit registration code
   * @return {Object} The POST request promise
   */
  confirmRegistrationCode(email, registrationCode) {
    return this.axios.post('/register/confirm', { email, registrationCode });
  }

  /**
   * Send a request to confirm the registration of a new admin originating from a partner service
   * sent to the admin via a confirmation email
   * @param {Object} payload - The data to be sent in the POST request
   * @param {String} payload.companySize - The admin's company size
   * @param {String} payload.country - The admin's country
   * @param {String} payload.email - The admin's email address
   * @param {String} payload.partnerType - SFDC partner type
   * @param {String} payload.registrationCode - The 6-digit registration code
   * @return {Object} The POST request promise
   */
  confirmPartnerRegistration(payload) {
    const mappedPayload = {
      company_size: payload.companySize,
      Country: payload.country,
      email: payload.email,
      Lead_Type__c: payload.partnerType || '',
      registrationCode: payload.registrationCode,
    };

    return this.axios.post('/register/confirm', mappedPayload);
  }

  /**
   * Resend an email to the admin containing a new 6-digit registration code
   * @param {String} email - The admin's email address
   * * @param {Boolean} partner - partner name
   * @return {Object} The POST request promise
   */
  resendRegistrationCode(email, partner = '') {
    return this.axios.post('/register/resend', { email, partner });
  }

  /**
   * Register a new admin.
   * @param {String} Free_Account__c - true
   * @param {String} GCLID__c - gclid value
   * @param {String} Initial_Campaign_Source__c: - query string cs, default 'Free.Account.Website'
   * @param {String} LeadSource - Default to 'Free Account'
   * @param {String} Referral_Source__c: query string rs, default 'Website',
   * @param {String} gclid_field - gclid
   * @param {String} license - 'on',
   * @param {Object} payload - The data to be sent in the POST request
   * @param {String} payload.company - The name of the admin's company
   * @param {String} payload.company_size - The admin's company size
   * @param {String} payload.email - The admin's email address
   * @param {String} payload.first_name - The admin's first name
   * @param {String} payload.last_name - The admin's last name
   * @param {String} payload.Country - The admin's country
   * @param {String} payload.state - The admin's state
   * @param {String} payload.dataCenter - The admin's data center selection ('us' or 'eu')
   * @param {String} payload.Double_Opt_In__c - The admin's selection to opt in to email (EU only)
   * @return {Object} The POST request promise
   */
  register(payload) {
    return this.axios.post('/register', payload);
  }

  /**
   * Admin registration verification when admin signs up via Google
   * @param  {Object} payload [containes user registration questions]
   * @return {Object} [returns the promise from the axios request]
   */
  newOrgVerification(payload) {
    return this.axios.post('/oauth/new-org-verification', payload);
  }
}

export default new SignupApi();
